import React, { useMemo } from "react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell,
  AreaChart, Area, PieChart, Pie, Legend, CartesianGrid,
} from "recharts";
import { GAMES } from "../../data/games";
import { computeFrequencies, computeGaps } from "../../utils/lottery";
import { TrendingUp, TrendingDown, Clock, PieChart as PieIcon, Star } from "lucide-react";

const Card = ({ title, icon, children, testId }) => (
  <div className="bg-white rounded-2xl border border-slate-200 p-5 sm:p-6 shadow-sm" data-testid={testId}>
    <h3 className="font-bold text-slate-900 flex items-center gap-2 mb-4">{icon} {title}</h3>
    {children}
  </div>
);

const BONUS_COLOR = { chance: "#EF4444", star: "#F59E0B", dream: "#9333EA" };

export const DashboardTab = ({ game, draws }) => {
  const cfg = GAMES[game];

  const { top, flop, gapsData, sumDist, parity, bonusFreq } = useMemo(() => {
    const freq = computeFrequencies(cfg, draws);
    const gaps = computeGaps(cfg, draws);
    const entries = Object.entries(freq).map(([n, c]) => ({ n: +n, c }));
    const sorted = [...entries].sort((a, b) => b.c - a.c);
    const top = sorted.slice(0, 10).map((e) => ({ num: `${e.n}`, count: e.c }));
    const flop = [...entries].sort((a, b) => a.c - b.c).slice(0, 10).map((e) => ({ num: `${e.n}`, count: e.c }));
    const gapsData = Object.entries(gaps).map(([n, g]) => ({ n: +n, g }))
      .sort((a, b) => b.g - a.g).slice(0, 10).map((e) => ({ num: `${e.n}`, gap: e.g }));

    // sum distribution
    const buckets = {};
    draws.forEach((d) => {
      const s = d.numbers.reduce((a, b) => a + b, 0);
      const key = Math.round(s / 10) * 10;
      buckets[key] = (buckets[key] || 0) + 1;
    });
    const sumDist = Object.entries(buckets).map(([s, c]) => ({ sum: +s, count: c })).sort((a, b) => a.sum - b.sum);

    // parity
    let even = 0, odd = 0;
    draws.forEach((d) => d.numbers.forEach((n) => (n % 2 === 0 ? even++ : odd++)));
    const parity = [{ name: "Pairs", value: even }, { name: "Impairs", value: odd }];

    // bonus number frequency (Chance / Étoiles / Dream)
    let bonusFreq = null;
    if (cfg.bonusPick > 0) {
      const bf = {};
      for (let n = 1; n <= cfg.bonusMax; n++) bf[n] = 0;
      draws.forEach((d) => (d.bonus || []).forEach((n) => { if (bf[n] !== undefined) bf[n] += 1; }));
      bonusFreq = Object.entries(bf).map(([n, c]) => ({ num: `${n}`, count: c }));
    }

    return { top, flop, gapsData, sumDist, parity, bonusFreq };
  }, [cfg, draws]);

  if (!draws.length) {
    return <div className="text-center py-16 text-slate-400 font-medium">Aucun tirage dans l'historique. L'administrateur doit ajouter des tirages.</div>;
  }

  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <Card title="Top 10 numéros les plus fréquents" icon={<TrendingUp className="w-5 h-5 text-emerald-500" />} testId="chart-top">
        <ResponsiveContainer width="100%" height={260}>
          <BarChart data={top}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eef2f7" />
            <XAxis dataKey="num" tick={{ fontSize: 12 }} />
            <YAxis tick={{ fontSize: 12 }} allowDecimals={false} />
            <Tooltip />
            <Bar dataKey="count" radius={[6, 6, 0, 0]}>
              {top.map((_, i) => <Cell key={i} fill={cfg.primary} />)}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </Card>

      <Card title="Top 10 numéros en retard (écart max)" icon={<Clock className="w-5 h-5 text-amber-500" />} testId="chart-gaps">
        <ResponsiveContainer width="100%" height={260}>
          <BarChart data={gapsData} layout="vertical">
            <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#eef2f7" />
            <XAxis type="number" tick={{ fontSize: 12 }} allowDecimals={false} />
            <YAxis type="category" dataKey="num" tick={{ fontSize: 12 }} width={30} />
            <Tooltip />
            <Bar dataKey="gap" radius={[0, 6, 6, 0]}>
              {gapsData.map((_, i) => <Cell key={i} fill={i < 3 ? "#F97316" : "#FBBF24"} />)}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </Card>

      <Card title="Distribution de la somme des tirages" icon={<TrendingDown className="w-5 h-5" style={{ color: cfg.secondary }} />} testId="chart-sum">
        <ResponsiveContainer width="100%" height={260}>
          <AreaChart data={sumDist}>
            <defs>
              <linearGradient id="sumG" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={cfg.secondary} stopOpacity={0.6} />
                <stop offset="95%" stopColor={cfg.secondary} stopOpacity={0.05} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eef2f7" />
            <XAxis dataKey="sum" tick={{ fontSize: 12 }} />
            <YAxis tick={{ fontSize: 12 }} allowDecimals={false} />
            <Tooltip />
            <Area type="monotone" dataKey="count" stroke={cfg.primary} strokeWidth={2} fill="url(#sumG)" />
          </AreaChart>
        </ResponsiveContainer>
      </Card>

      <Card title="Répartition Pairs / Impairs" icon={<PieIcon className="w-5 h-5" style={{ color: cfg.primary }} />} testId="chart-parity">
        <ResponsiveContainer width="100%" height={260}>
          <PieChart>
            <Pie data={parity} dataKey="value" nameKey="name" innerRadius={60} outerRadius={95} paddingAngle={3}>
              <Cell fill={cfg.primary} />
              <Cell fill="#10B981" />
            </Pie>
            <Legend />
            <Tooltip />
          </PieChart>
        </ResponsiveContainer>
      </Card>

      {bonusFreq && (
        <Card title={`Fréquence des ${cfg.bonusName}`} icon={<Star className="w-5 h-5" style={{ color: BONUS_COLOR[cfg.bonusType] || cfg.secondary }} />} testId="chart-bonus">
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={bonusFreq}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eef2f7" />
              <XAxis dataKey="num" tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 12 }} allowDecimals={false} />
              <Tooltip />
              <Bar dataKey="count" radius={[6, 6, 0, 0]}>
                {bonusFreq.map((_, i) => <Cell key={i} fill={BONUS_COLOR[cfg.bonusType] || cfg.secondary} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Card>
      )}
    </div>
  );
};
